import dayjs, { Dayjs } from "dayjs";
import { atomWithStorage } from "jotai/utils";
import { StorageKeys } from "./storage";

export type FiscalYear = {
	id: number;
	end: Dayjs;
	start: Dayjs;
};

export type UserPeriodFilterType = "custom" | "fiscal";

export type UserPeriodFilter = {
	// The selected period identifier.
	selectedPeriodId: number;

	// Fiscal years indicate the fiscal years available for the user.
	fiscalYears: FiscalYear[];

	// Type indicates the period filter type selected by the user: fiscal or custom.
	type: UserPeriodFilterType;

	// Start and end dates indicate the **current** period set by the user.
	start: Dayjs;
	end: Dayjs;
};

/**
 * Returns the user period filter by reading the local storage.
 */
export const getUserPeriodFilter = (): UserPeriodFilter | undefined => {
	const raw = localStorage.getItem(StorageKeys.PeriodFilter);

	try {
		const stored = JSON.parse(raw ?? "") as UserPeriodFilter;
		const fiscalYears = stored.fiscalYears.map((fy) => {
			return {
				id: fy.id,
				end: dayjs(fy.end),
				start: dayjs(fy.start),
			} as FiscalYear;
		});
		const periodFilter = {
			...stored,
			start: dayjs(stored.start),
			end: dayjs(stored.end),
			fiscalYears: fiscalYears,
		};
		return periodFilter as UserPeriodFilter;
	} catch {
		console.warn("cannot parse user period filter stored in the local storage");
		return undefined;
	}
};

/**
 * Holds the user current fiscal year.
 */
export const userPeriodFilter = atomWithStorage<UserPeriodFilter | undefined>(
	StorageKeys.PeriodFilter,
	undefined,
	{
		getItem(key) {
			return getUserPeriodFilter();
		},
		setItem(key, newValue) {
			if (!newValue) {
				localStorage.removeItem(key);
			} else {
				localStorage.setItem(key, JSON.stringify(newValue));
			}
		},
		removeItem(key) {
			localStorage.removeItem(key);
		},
	},
	// Read the stored value on first render, avoiding a flash of the default value (and duplicated requests).
	{ getOnInit: true }
);
